import { useContext, useEffect } from "react"
import type { Cart } from "../components/Cart"
import {
  type BaseCommunityFragment,
  type BaseProductFragment,
  ProductDocument,
  type ProductQuery,
  type ProductQueryVariables,
  ProductType,
} from "../generated/graphql"
import { graphQLClient } from "../graphql/client"
import { UserContext } from "../providers/UserProvider"
import { useCommunityFromCode } from "./useCommunityFromCode"

// Mirrors api/src/utils/request-payment.ts `getJoinRequiredProductIds` — the products a join
// request must buy one of. Set: joining requires a purchase (CLUB M). Absent: any ticket counts
// and free applications stay open (Serpent's Nest).
const getJoinRequiredProductIds = (configs: unknown): string[] => {
  if (!configs || typeof configs !== "object" || Array.isArray(configs)) {
    return []
  }
  const ids = (configs as Record<string, unknown>).requestToJoinRequiredProductIds
  return Array.isArray(ids)
    ? ids.filter((id): id is string => typeof id === "string" && id.length > 0)
    : []
}

/**
 * Where the gate's Join button sends a non-member of a community where joining requires a
 * purchase: the latest event among the listed products, else the event list.
 * `undefined` when joining doesn't require a purchase (the gate runs the normal application).
 * `pathCode` is the code in the current URL (the landing code for most communities).
 */
export const resolveJoinPurchasePath = async (
  community: Pick<BaseCommunityFragment, "configs" | "requestToJoin"> | null | undefined,
  pathCode: string | undefined,
): Promise<string | undefined> => {
  const productIds = community?.requestToJoin ? getJoinRequiredProductIds(community.configs) : []
  if (!pathCode || productIds.length === 0) {
    return undefined
  }
  const products = await Promise.allSettled(
    productIds.map((productId) =>
      graphQLClient.request<ProductQuery, ProductQueryVariables>(ProductDocument, { productId }),
    ),
  )
  const latestEvent = products
    .flatMap((result) =>
      result.status === "fulfilled" && result.value.product?.event
        ? [result.value.product.event]
        : [],
    )
    .sort((a, b) => new Date(b.start ?? 0).getTime() - new Date(a.start ?? 0).getTime())[0]
  return latestEvent ? `/c/${pathCode}/event/${latestEvent.id}` : `/c/${pathCode}/events`
}

// Mirrors api/src/utils/cart.ts — the ticket's "sells together with" pairing
// that drives the auto-added membership line.
const getRequiredTogetherProductId = (additionalConfig: unknown): string | undefined => {
  if (
    !additionalConfig ||
    typeof additionalConfig !== "object" ||
    Array.isArray(additionalConfig)
  ) {
    return undefined
  }
  const value = (additionalConfig as Record<string, unknown>).requiredTogetherProductId
  return typeof value === "string" && value.length > 0 ? value : undefined
}

export const useRequestCheckout = () => {
  const { communities } = useContext(UserContext)
  const { community } = useCommunityFromCode()
  const isCommunityMember = communities.some(
    (memberCommunity) => memberCommunity.code === community?.code,
  )
  const communityLoaded = Boolean(community)
  // A non-member's checkout in a request-to-join community is a join request.
  const requestMode = Boolean(community?.requestToJoin && !isCommunityMember)

  return {
    community,
    communityLoaded,
    requestMode,
  }
}

export const getCartMembershipProduct = (cart: Cart, code?: string) =>
  code
    ? Object.values(cart[code] ?? {}).find(
        ({ product, quantity }) => quantity > 0 && product.productType === ProductType.Membership,
      )?.product
    : undefined

/**
 * Keeps the cart's membership lines consistent with the pairing rule, in BOTH checkout
 * modes: a membership line exists (qty 1) exactly while a ticket pointing at it via
 * `requiredTogetherProductId` is in the cart and the viewer-specific API response
 * includes that membership.
 *
 * Deliberately NOT keyed on request mode or community membership: a community member
 * without a paid membership (manual Super Admin add, transferred-ticket recipient) still
 * owes the membership with their ticket. Callers without viewer eligibility data retain
 * the pairing-only behavior; event pages pass `bundleProducts` as the API source of truth.
 */
export const useSyncRequestCartMembership = ({
  adjustCart,
  bundleProducts,
  cart,
  code,
  communityLoaded,
  membershipProduct,
}: {
  adjustCart: (code: string, product: BaseProductFragment, quantity: number) => void
  bundleProducts?: readonly BaseProductFragment[]
  cart: Cart
  code?: string
  communityLoaded: boolean
  membershipProduct?: BaseProductFragment
}) => {
  const communityCart = code ? cart[code] : undefined

  useEffect(() => {
    if (!code || !communityLoaded) return

    const cartItems = Object.values(communityCart ?? {})
    const resolvedBundleProductIds = bundleProducts
      ? new Set(bundleProducts.map((product) => product.id))
      : undefined
    const membershipLineRequired = (membershipProductId: string) =>
      cartItems.some(
        ({ product, quantity }) =>
          quantity > 0 &&
          getRequiredTogetherProductId(product.additionalConfig) === membershipProductId,
      )
    const viewerRequiresMembershipLine = (membershipProductId: string) =>
      !resolvedBundleProductIds || resolvedBundleProductIds.has(membershipProductId)

    // Existing membership lines (including stale localStorage leftovers): keep only
    // while both the ticket pairing and viewer-specific API response require them,
    // always at qty 1. Memberships are never standalone cart items — Product.tsx
    // routes them to checkout-subscribe.
    for (const item of cartItems) {
      if (item.product.productType !== ProductType.Membership) continue
      const required =
        membershipLineRequired(item.product.id) && viewerRequiresMembershipLine(item.product.id)
      if (!required && item.quantity > 0) {
        adjustCart(code, item.product, -item.quantity)
      } else if (required && item.quantity !== 1) {
        adjustCart(code, item.product, 1 - item.quantity)
      }
    }

    // Auto-add the paired membership when a paired ticket is in the cart but the
    // membership line is missing entirely.
    if (membershipProduct) {
      const membershipQuantity = communityCart?.[membershipProduct.id]?.quantity ?? 0
      if (membershipLineRequired(membershipProduct.id) && membershipQuantity !== 1) {
        adjustCart(code, membershipProduct, 1 - membershipQuantity)
      }
    }
  }, [adjustCart, bundleProducts, code, communityCart, communityLoaded, membershipProduct])
}
